import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import useTranslation from 'Hooks/useTranslation';
import { DragIndicator, Delete, AddCircle } from '@mui/icons-material';
import { FormHelperText } from '@mui/material';
import { Draggable } from 'react-beautiful-dnd';
import TextInput from 'Components/FormComponents/TextInput';
import SelectInput from 'Components/FormComponents/SelectInput';
import { MULTIPLE_CHOICE, FREE_RESPONSE, MULTIPLE_CHOICE_ID, FREE_RESPONSE_ID } from 'Constants/General';
import { NO_EMPTY_NO_EMPTY_SPACES } from 'Constants/Regex';
import QuestionTypes from './QuestionTypes/index';
import {
  QuestionContainer,
  DragContainer,
  QuestionSection,
  QuestionFooter,
  FloatingTrashButton,
  FloatingAddButton,
  horizontalDrag,
  questionSelectInput,
  questionTextInput,
} from './style';

function Question({ question, questions, setQuestions, formData, setFormData, setUnchanged, isLastItem, position }) {
  const { t2 } = useTranslation('EditCourse');
  const questionTypes = [
    {
      id: MULTIPLE_CHOICE_ID,
      name: t2('multipleChoice'),
    },
    {
      id: FREE_RESPONSE_ID,
      name: t2('freeResponse'),
    },
  ];
  const questionTypesId = {
    [`${MULTIPLE_CHOICE_ID}`]: MULTIPLE_CHOICE,
    [`${FREE_RESPONSE_ID}`]: FREE_RESPONSE,
  };
  const [questionData, setQuestionData] = useState({ ...question });
  const [selectedType, setSelectedType] = useState(
    question?.evaluationQuestionTypeId !== '' ? questionTypesId[question.evaluationQuestionTypeId] : ''
  );

  useEffect(() => {
    setQuestionData({ ...question });
  }, [question]);

  const setQuestionType = event => {
    setSelectedType(questionTypesId[event.target.value]);

    const formDataCopy = _.cloneDeep(formData);
    formDataCopy.questions[position] = {
      ...questionData,
      evaluationQuestionTypeId: event.target.value,
    };
    setUnchanged(false);
    setFormData(formDataCopy);
  };

  const handleChange = event => {
    const targetName = event.target.name;
    const targetValue =
      event.target.name === 'value' && event.target.value > 0 ? parseInt(event.target.value, 10) : event.target.value;
    setQuestionData({ ...questionData, [targetName]: targetValue });
  };

  const addQuestion = () => {
    const questionShell = {
      id: `question_${uuidv4()}`,
      order: question.order + 1,
      answers: [
        {
          id: `question_${question.order}-answer_${uuidv4()}`,
          order: 1,
          isCorrect: false,
          response: '',
        },
      ],
      value: 1,
      question: `${t2('Question')} #${question.order + 1}`,
      evaluationQuestionTypeId: MULTIPLE_CHOICE_ID,
    };
    const formDataCopy = _.cloneDeep(formData);
    formDataCopy.questions = [...questions, questionShell];
    setFormData(formDataCopy);
    setUnchanged(false);
  };

  const deleteQuestion = () => {
    const formDataCopy = _.cloneDeep(formData);
    formDataCopy.questions.splice(position, 1);
    setFormData(formDataCopy);
    setUnchanged(false);
  };

  const onQuestionBlur = event => {
    const formDataCopy = _.cloneDeep(formData);
    const targetValue = event.target.value;
    formDataCopy.questions[position] = {
      ...questionData,
      question: targetValue,
    };

    if (questionData.question.match(NO_EMPTY_NO_EMPTY_SPACES) || questionData.question.length < 6) {
      setFormData({
        ...formDataCopy,
        errors: {
          ...formDataCopy.errors,
          [`question-${question.id}_question`]: t2('FormErrorEvaluationQuestion'),
        },
      });
      return;
    }
    delete formDataCopy.errors[`question-${question.id}_question`];

    setUnchanged(false);
    setFormData({ ...formDataCopy });
  };

  const onValueBlur = event => {
    const formDataCopy = _.cloneDeep(formData);
    const targetValue = parseInt(event.target.value, 10);
    formDataCopy.questions[position] = {
      ...questionData,
      value: targetValue,
    };

    if (targetValue <= 0 || !Number.isInteger(targetValue)) {
      setFormData({
        ...formDataCopy,
        errors: {
          ...formData.errors,
          [`question-${question.id}_value`]: t2('FormErrorEvaluationValue'),
        },
      });
    } else {
      delete formDataCopy.errors[`question-${question.id}_value`];

      setUnchanged(false);
      setFormData({ ...formDataCopy });
    }
  };

  return (
    <Draggable key={question.id} draggableId={question.id} index={position}>
      {provided => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <QuestionContainer>
            <DragContainer {...provided.dragHandleProps}>
              <DragIndicator style={horizontalDrag} />
            </DragContainer>
            <QuestionSection>
              <TextInput
                name="question"
                label={t2('Question')}
                color="MexlogGreen"
                value={questionData?.question}
                onChange={handleChange}
                style={questionTextInput}
                multiline
                onBlur={onQuestionBlur}
                errortext={formData.errors[`question-${question.id}_question`]}
              />
              <SelectInput
                name="evaluationQuestionTypeId"
                placeholder={t2('QuestionType')}
                value={questionData?.evaluationQuestionTypeId}
                onChange={setQuestionType}
                disableunderline="true"
                options={questionTypes}
                style={questionSelectInput}
              />
            </QuestionSection>
            <QuestionTypes
              type={selectedType}
              question={question}
              questions={questions}
              setQuestions={setQuestions}
              setFormData={setFormData}
              setUnchanged={setUnchanged}
              formData={formData}
              positionQuestion={position}
            />
            <QuestionFooter>
              <TextInput
                name="value"
                type="tel"
                inputProps={{
                  min: 1,
                  max: 99,
                  maxLength: 2,
                  style: { textAlign: 'center' },
                }}
                color="MexlogGreen"
                value={questionData?.value}
                onChange={handleChange}
                sx={{ width: '4rem' }}
                size="small"
                onBlur={onValueBlur}
              />
              <div>{t2('QuestionValue')}</div>
              {questions.length !== 1 && (
                <FloatingTrashButton>
                  <Delete sx={{ width: 30, height: 30 }} onClick={deleteQuestion} />
                </FloatingTrashButton>
              )}
            </QuestionFooter>
            {formData.errors[`question-${question.id}_value`] && (
              <FormHelperText sx={{ color: 'red' }}>{formData.errors[`question-${question.id}_value`]}</FormHelperText>
            )}
            {isLastItem && (
              <FloatingAddButton>
                <AddCircle color="MexlogGreen" sx={{ width: 50, height: 50 }} onClick={addQuestion} />
              </FloatingAddButton>
            )}
          </QuestionContainer>
        </div>
      )}
    </Draggable>
  );
}

export default Question;
